import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { SheetTitle } from "../../../../components/ui/sheet";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { FormPhoneInput } from "../../../../components/form-components/form-phone-input.components";
import { useEffect } from "react";
import Spinner from "../../../../components/spinner.components";
import { handleHttpError } from "../../../../utils/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/ui/form";
import SoloThunderIcon from "../../../../assets/svgs/common/solo-thunder.svg";
import {
  useCreateVendorHttp,
  VendorType,
} from "../../../../hooks/http/admin/vendors/create-vendor.admin.http";
import useVendorsStore from "../../../../store/vendors.store";
import StarIcon from "../../../../assets/svgs/common/star.svg";
// Import the SoloIcon

const FormSchema = z.object({
  name: z.string().min(1, { message: "Enter vendor name" }),
  email: z.string().email({ message: "Invalid email address" }),
  type: z.nativeEnum(VendorType, {
    errorMap: () => ({ message: "Please select a vendor type" }),
  }),
  phoneNumber: z
    .string()
    .min(10, { message: "Enter a valid contact number" })
    .max(10, { message: "Enter a valid contact number" }),
});

export default function AddVendorSliderAdminPartial() {
  const { execute: createVendorHttp, fetching: creatingVendor } =
    useCreateVendorHttp();

  const { addVendor } = useVendorsStore();

  const { toast } = useToast();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      type: VendorType.AGENCY,
    },

  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    const response = await createVendorHttp({
      body: {
        name: data.name,
        email: data.email,
        vendorType: data.type,
        phoneNumber: data.phoneNumber,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({
      content: "Vendor created successfully",
      toastType: "success",
    });
    addVendor(response.data.vendor);
    document.getElementById("closeSheet")?.click();
  }

  // Show form validation errors as toast
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors, toast]);

  return (
    <div className="flex flex-col w-full h-full">
      <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
        Add vendors
      </SheetTitle>

      <div className="flex flex-col gap-10 p-5 w-full h-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col justify-between gap-8 w-full h-full"
          >
            <div className="flex flex-col gap-6 w-full">
              {/* Vendor Name */}
              <FormInput
                name="name"
                label="Vendor name"
                placeholder="Enter vendor name here"
                required
                form={form}
                type="text"
              />

              {/* Vendor Email */}
              <FormInput
                name="email"
                label="Vendor email"
                placeholder="xyz@gmail.com"
                required
                form={form}
                type="email"
              />

              {/* Select Type */}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="flex items-center font-medium text-white">
                      Select type <StarIcon/>
                    </FormLabel>
                    <FormControl>
                      <div className="gap-4 grid grid-cols-2">
                        {/* Agency Option */}
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                        <div
                          className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                            field.value === VendorType.AGENCY
                              ? " border-border hover:border-white/50"
                              : "border-border bg-soloprimary/10"
                          }`}
                          onClick={() => field.onChange(VendorType.AGENCY)}
                        >
                          <div className="flex justify-center items-center mt-0.5">
                            <div
                              className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${
                                field.value === VendorType.AGENCY
                                  ? "border-soloprimary bg-soloprimary"
                                  : "border-gray-400"
                              }`}
                            >
                              {field.value === VendorType.AGENCY && (
                                <SoloThunderIcon />
                              )}
                            </div>
                          </div>
                          <div className="gap-1.5 grid leading-none">
                            <label className="font-medium text-white cursor-pointer">
                              Agency
                            </label>
                            <p className="text-gray-400 text-sm">
                              Lorem ipsum is a dummy or place holder text
                              commonly
                            </p>
                          </div>
                        </div>
                        {/* Individual Option */}
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                        <div
                          className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                            field.value === VendorType.INDIVIDUAL
                              ? " border-border hover:border-white/50"
                              : "border-border bg-soloprimary/10"
                          }`}
                          onClick={() => field.onChange(VendorType.INDIVIDUAL)}
                        >
                          <div className="flex justify-center items-center mt-0.5">
                            <div
                              className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${
                                field.value === VendorType.INDIVIDUAL
                                  ? "border-soloprimary bg-soloprimary"
                                  : "border-gray-400"
                              }`}
                            >
                              {field.value === VendorType.INDIVIDUAL && (
                                <SoloThunderIcon />
                              )}
                            </div>
                          </div>
                          <div className="gap-1.5 grid leading-none">
                            <label className="font-medium text-white cursor-pointer">
                              Individual
                            </label>
                            <p className="text-gray-400 text-sm">
                              Lorem ipsum is a dummy or place holder text
                              commonly
                            </p>
                          </div>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contact Number */}
              <FormPhoneInput
                name="phoneNumber"
                label="Phone number"
                placeholder="Enter number here"
                required
                form={form}
              />
            </div>

            {/* Submit Button */}
            <Button
              disabled={creatingVendor || !form.formState.isValid}
              type="submit"
              className="flex gap-2 bg-primarybuttonbg py-8 w-full font-semibold text-primarybuttontext"
            >
              {creatingVendor ? <Spinner width={20} /> : "Add Vendor →"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
